import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import {
  createRockPipeBoard,
  finishRotationForSide,
  isRockPipePathConnected,
  ROCK_PUZZLE_COLS,
  ROCK_PUZZLE_TIME_LIMIT_SEC,
  ROCK_VICTORY_HOLD_MS,
  rotateRockPipeTileAt,
  type RockPipeBoard,
  type RockPipeSide,
} from '../../../../domain';
import {
  getRockPipeShapeUrl,
  ROCK_FINISH_URL,
  ROCK_HEADER_URL,
  ROCK_HINT_URL,
  ROCK_TILE_URL,
  ROCK_TIMER_URL,
  ROCK_VICTORY_URL,
} from '../assets';
import './RockRotatePuzzleGame.css';

export type RockRotatePuzzleGameProps = {
  onWin: () => void;
  onLose: () => void;
  onTimerChange?: (secondsLeft: number) => void;
};

type Phase = 'play' | 'victory';

/** Cell plates from .dev/rock/mini-game.svg masks (2×3). */
const CELL_SLOTS: ReadonlyArray<{ left: number; top: number; size: number }> = [
  { left: 265, top: 627, size: 275 },
  { left: 540, top: 627, size: 275 },
  { left: 264, top: 902, size: 275 },
  { left: 540, top: 902, size: 275 },
  { left: 265, top: 1177, size: 275 },
  { left: 539, top: 1175, size: 275 },
];

/** Circle center inside finish.png (circle on the right, stub on the left at 0°). */
const FINISH_CIRCLE_X = 152;
const FINISH_CIRCLE_Y = 63;

function formatRockTimer(secondsLeft: number): string {
  const safe = Math.max(0, secondsLeft);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} сек`;
}

function finishAnchor(
  slot: { left: number; top: number; size: number },
  side: RockPipeSide,
): { cx: number; cy: number } {
  switch (side) {
    case 'left':
      return { cx: slot.left, cy: slot.top + slot.size / 2 };
    case 'right':
      return { cx: slot.left + slot.size, cy: slot.top + slot.size / 2 };
    case 'top':
      return { cx: slot.left + slot.size / 2, cy: slot.top };
    case 'bottom':
      return { cx: slot.left + slot.size / 2, cy: slot.top + slot.size };
    default: {
      const _exhaustive: never = side;
      return _exhaustive;
    }
  }
}

function finishStyle(
  slot: { left: number; top: number; size: number },
  side: RockPipeSide,
): CSSProperties {
  const { cx, cy } = finishAnchor(slot, side);
  return {
    left: cx - FINISH_CIRCLE_X,
    top: cy - FINISH_CIRCLE_Y,
    transform: `rotate(${finishRotationForSide(side)}deg)`,
  };
}

export function RockRotatePuzzleGame({
  onWin,
  onLose,
  onTimerChange,
}: RockRotatePuzzleGameProps) {
  const [board, setBoard] = useState<RockPipeBoard>(() => createRockPipeBoard());
  const [secondsLeft, setSecondsLeft] = useState<number>(ROCK_PUZZLE_TIME_LIMIT_SEC);
  const [phase, setPhase] = useState<Phase>('play');
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    onTimerChange?.(secondsLeft);
  }, [onTimerChange, secondsLeft]);

  useEffect(() => {
    if (finished || phase !== 'play') {
      return;
    }
    const timer = window.setInterval(() => {
      setSecondsLeft((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [finished, phase]);

  useEffect(() => {
    if (finished || phase !== 'play' || secondsLeft > 0) {
      return;
    }
    setFinished(true);
    onLose();
  }, [finished, onLose, phase, secondsLeft]);

  useEffect(() => {
    if (phase !== 'victory') {
      return;
    }
    const timer = window.setTimeout(() => {
      onWin();
    }, ROCK_VICTORY_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [onWin, phase]);

  const handleRotate = useCallback(
    (index: number) => {
      if (finished || phase !== 'play') {
        return;
      }
      setBoard((prev) => {
        const next = rotateRockPipeTileAt(prev, index);
        if (isRockPipePathConnected(next)) {
          window.setTimeout(() => {
            setFinished(true);
            setPhase('victory');
          }, 0);
        }
        return next;
      });
    },
    [finished, phase],
  );

  const endpoints = useMemo(() => {
    const { a, b } = board.variant;
    const indexA = a.row * ROCK_PUZZLE_COLS + a.col;
    const indexB = b.row * ROCK_PUZZLE_COLS + b.col;
    return [
      { key: 'a', slot: CELL_SLOTS[indexA] ?? CELL_SLOTS[0]!, side: a.side },
      { key: 'b', slot: CELL_SLOTS[indexB] ?? CELL_SLOTS[0]!, side: b.side },
    ];
  }, [board.variant]);

  return (
    <div className="rock-pipe">
      <img className="rock-pipe__tile" src={ROCK_TILE_URL} alt="" draggable={false} />

      {board.cells.map((cell, index) => {
        const slot = CELL_SLOTS[index] ?? CELL_SLOTS[0]!;
        const isPipe = cell.shape !== 'empty';
        return (
          <button
            key={index}
            type="button"
            className={`rock-pipe__cell${isPipe ? ' is-pipe' : ''}`}
            style={{ left: slot.left, top: slot.top, width: slot.size, height: slot.size }}
            onClick={() => handleRotate(index)}
            disabled={finished || phase !== 'play' || !isPipe}
            aria-label={isPipe ? `Деталь ${index + 1}` : undefined}
          >
            {cell.shape !== 'empty' ? (
              <img
                className="rock-pipe__cell-art"
                src={getRockPipeShapeUrl(cell.shape)}
                alt=""
                draggable={false}
                style={{ transform: `rotate(${cell.rotation * 90}deg)` }}
              />
            ) : null}
          </button>
        );
      })}

      {endpoints.map((ep) => (
        <img
          key={ep.key}
          className="rock-pipe__finish"
          src={ROCK_FINISH_URL}
          alt=""
          draggable={false}
          style={finishStyle(ep.slot, ep.side)}
        />
      ))}

      <img className="rock-pipe__header" src={ROCK_HEADER_URL} alt="" draggable={false} />
      <h2 className="rock-pipe__title">Почини трубы</h2>
      {phase === 'play' ? (
        <img className="rock-pipe__hint" src={ROCK_HINT_URL} alt="" draggable={false} />
      ) : null}

      <div className="rock-pipe__timer">
        <img className="rock-pipe__timer-art" src={ROCK_TIMER_URL} alt="" draggable={false} />
        <p className="rock-pipe__timer-value">{formatRockTimer(secondsLeft)}</p>
      </div>

      {phase === 'victory' ? (
        <div className="rock-pipe__victory" aria-live="polite">
          <img
            className="rock-pipe__victory-art"
            src={ROCK_VICTORY_URL}
            alt="Победа"
            draggable={false}
          />
        </div>
      ) : null}
    </div>
  );
}
